import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api, { rupee } from "@/lib/api";
import { StatCard } from "@/components/DashboardShell";
import { Spinner } from "@/components/States";
import { IndianRupee, ShoppingCart, TrendingUp, UserPlus } from "lucide-react";

const COLORS = ["#059669", "#F59E0B", "#3B82F6", "#8B5CF6", "#EF4444", "#14B8A6", "#EC4899"];
const Card = ({ title, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <h3 className="mb-3 font-head font-bold text-slate-900">{title}</h3>
    {children}
  </div>
);

export default function AdminReports() {
  const [range, setRange] = useState("30");
  const { data: d } = useQuery({ queryKey: ["a-reports", range], queryFn: () => api.get("/admin/reports", { params: { range } }).then((r) => r.data) });
  if (!d) return <Spinner />;

  const statusData = Object.entries(d.status_split).map(([k, v]) => ({ name: k.replace(/_/g, " "), value: v }));
  const payData = Object.entries(d.payment_split).map(([k, v]) => ({ name: k, orders: v.count, amount: v.amount }));

  return (
    <div className="space-y-5" data-testid="admin-reports-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Business reports & analytics</p>
        <select value={range} onChange={(e) => setRange(e.target.value)} data-testid="reports-range" className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600">
          <option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard label="GMV (delivered)" value={rupee(d.totals.gmv)} icon={IndianRupee} color="#059669" />
        <StatCard label="Orders" value={d.totals.orders} icon={ShoppingCart} color="#3B82F6" />
        <StatCard label="Avg Order Value" value={rupee(d.totals.aov)} icon={TrendingUp} color="#8B5CF6" />
        <StatCard label="Delivered / Cancelled" value={`${d.totals.delivered} / ${d.totals.cancelled}`} icon={ShoppingCart} color="#F59E0B" />
        <StatCard label="New Customers" value={d.totals.new_customers} icon={UserPlus} color="#EC4899" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="GMV trend">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.daily}><XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Line type="monotone" dataKey="gmv" stroke="#059669" strokeWidth={3} dot={false} /></LineChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Orders per day">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.daily}><XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Bar dataKey="orders" fill="#3B82F6" radius={[6, 6, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Order status split">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart><Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>{statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Legend /></PieChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Payments by method">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={payData}><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Bar dataKey="amount" fill="#F59E0B" radius={[6, 6, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Top products (by units)">
          {d.top_products.length ? d.top_products.map((p, i) => (
            <div key={i} className="flex items-center justify-between border-b border-slate-50 py-2 text-sm last:border-0">
              <span className="font-semibold text-slate-700">{i + 1}. {p.name}</span>
              <span className="text-slate-500">{p.units} sold · <b className="text-slate-800">{rupee(p.revenue)}</b></span>
            </div>
          )) : <p className="text-sm text-slate-400">No sales yet in this period.</p>}
        </Card>
        <Card title="Top stores (by revenue)">
          {d.top_stores.length ? d.top_stores.map((s, i) => (
            <div key={i} className="flex items-center justify-between border-b border-slate-50 py-2 text-sm last:border-0">
              <span className="font-semibold text-slate-700">{i + 1}. {s.name}</span>
              <span className="text-slate-500">{s.orders} orders · <b className="text-slate-800">{rupee(s.revenue)}</b></span>
            </div>
          )) : <p className="text-sm text-slate-400">No revenue yet in this period.</p>}
        </Card>
      </div>

      {d.category_sales.length > 0 && (
        <Card title="Sales by category">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.category_sales} layout="vertical"><XAxis type="number" fontSize={11} /><YAxis type="category" dataKey="category" fontSize={11} width={120} /><Tooltip /><Bar dataKey="revenue" fill="#8B5CF6" radius={[0, 6, 6, 0]} /></BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}
